import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import styles from "./PlanrTooltip.module.scss";

/**
 * Фирменная подсказка: сетка плана заводит её на весь контейнер, списки — на грид.
 *
 * Нативный `title` здесь не годится: браузер держит паузу около секунды и рисует
 * подсказку системным мелким шрифтом. На узких блоках (день = 30px) подпись
 * влезает не всегда, и подсказка становится единственным способом узнать проект —
 * значит она обязана появляться сразу и читаться.
 *
 * Слушатели висят на контейнере с делегированием: блоков в сетке сотни,
 * вешать обработчики на каждый — лишняя работа при каждой перерисовке.
 */

/** Пауза перед показом: меньше — подсказка мигает при проходе курсора по сетке */
const OPEN_DELAY = 140;

/** Отступ подсказки от курсора и от краёв окна */
const CURSOR_GAP = 14;
const VIEWPORT_GAP = 8;

/** Атрибуты, которыми элементы сетки объявляют свою подсказку */
export const TIP_ATTR = "data-tip";
export const TIP_NOTE_ATTR = "data-tip-note";

interface TipState {
    text: string;
    note: string;
    x: number;
    y: number;
}

interface PlanrTooltipProps {
    /** Контейнер: подсказки ищутся среди его потомков по атрибуту data-tip */
    containerRef: React.RefObject<HTMLElement>;
}

export const PlanrTooltip: React.FC<PlanrTooltipProps> = ({ containerRef }) => {
    const [tip, setTip] = useState<TipState | null>(null);
    const [position, setPosition] = useState<React.CSSProperties>({ visibility: "hidden" });
    const rootRef = useRef<HTMLDivElement>(null);
    const timer = useRef<number>();
    // во время перетаскивания подсказка только мешает — курсор занят жестом
    const suspended = useRef(false);

    const hide = useCallback(() => {
        window.clearTimeout(timer.current);
        setTip(null);
    }, []);

    useEffect(() => {
        const container = containerRef.current;

        if (!container) {
            return;
        }

        const onOver = (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;
            const source = target && target.closest ? target.closest(`[${TIP_ATTR}]`) : null;
            const text = source ? source.getAttribute(TIP_ATTR) : null;

            if (!source || !text) {
                hide();
                return;
            }

            const note = source.getAttribute(TIP_NOTE_ATTR) || "";
            const rect = source.getBoundingClientRect();
            // по горизонтали — за курсором, по вертикали — под элементом:
            // так подсказка не дёргается, пока курсор ходит вдоль блока
            const next = { text, note, x: event.clientX, y: rect.bottom + 6 };

            window.clearTimeout(timer.current);
            timer.current = window.setTimeout(() => {
                if (!suspended.current) {
                    setPosition({ visibility: "hidden" });
                    setTip(next);
                }
            }, OPEN_DELAY);
        };

        const onDown = () => {
            suspended.current = true;
            hide();
        };

        const onUp = () => {
            suspended.current = false;
        };

        container.addEventListener("mouseover", onOver);
        container.addEventListener("mouseleave", hide);
        document.addEventListener("pointerdown", onDown, true);
        document.addEventListener("pointerup", onUp, true);
        window.addEventListener("scroll", hide, true);

        return () => {
            window.clearTimeout(timer.current);
            container.removeEventListener("mouseover", onOver);
            container.removeEventListener("mouseleave", hide);
            document.removeEventListener("pointerdown", onDown, true);
            document.removeEventListener("pointerup", onUp, true);
            window.removeEventListener("scroll", hide, true);
        };
    }, [containerRef, hide]);

    // у правого и нижнего краёв окна подсказка разворачивается внутрь экрана
    useLayoutEffect(() => {
        const element = rootRef.current;

        if (!tip || !element) {
            return;
        }

        const { width, height } = element.getBoundingClientRect();
        const left = Math.max(VIEWPORT_GAP, Math.min(tip.x + CURSOR_GAP, window.innerWidth - width - VIEWPORT_GAP));
        const top =
            tip.y + height + VIEWPORT_GAP <= window.innerHeight ? tip.y : Math.max(VIEWPORT_GAP, tip.y - height - 26);

        setPosition({ left, top, visibility: "visible" });
    }, [tip]);

    if (!tip) {
        return null;
    }

    return ReactDOM.createPortal(
        <div className={styles.tooltip} style={position} ref={rootRef}>
            <div className={styles.text}>{tip.text}</div>
            {tip.note && <div className={styles.note}>{tip.note}</div>}
        </div>,
        document.body
    );
};
